import { useEffect, useState } from "react";
import { GitMerge, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { estApi } from "../lib/api";

/** Directory › Possible duplicates: fuzzy name matches + companies auto-created from ITBs awaiting review; one-click merge. */
export default function DuplicatesPanel({ onClose, onMerged }) {
  const [d, setD] = useState(null);
  const load = () => estApi.directoryDuplicates().then(setD).catch(() => toast.error("Could not load duplicates"));
  useEffect(() => { load(); }, []);
  const merge = async (keep, gone) => {
    if (!window.confirm(`Merge “${gone.name}” into “${keep.name}”? Contacts, bids, estimates, pipeline, budgets and BOD rows are re-pointed; “${gone.name}” becomes an alias.`)) return;
    try { const r = await estApi.mergeCompanies(keep.id, gone.id); toast.success(`Merged — ${r.records_repointed} records re-pointed`); load(); onMerged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Merge failed"); }
  };
  return (
    <div className="fixed inset-y-0 right-0 z-40 flex w-full max-w-xl flex-col border-l border-slate-200 bg-white shadow-xl" data-testid="duplicates-panel">
      <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
        <GitMerge className="h-4 w-4 text-slate-500" /><h2 className="text-sm font-semibold">Possible duplicates</h2>
        <button onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-700" data-testid="duplicates-close"><X className="h-4 w-4" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 text-xs">
        {!d ? <div className="text-slate-400">Scanning…</div> : (<>
          {d.needs_review.length > 0 && (
            <div className="mb-4">
              <div className="mb-1 font-semibold text-slate-600">New from ITB — confirm or merge ({d.needs_review.length})</div>
              {d.needs_review.map((c) => <div key={c.id} className="rounded border border-amber-200 bg-amber-50 px-2 py-1 mb-1" data-testid={`review-${c.id}`}>{c.name}</div>)}
            </div>)}
          {d.pairs.length === 0 && <div className="text-slate-400">No likely duplicates.</div>}
          {d.pairs.map((p, i) => (
            <div key={`${p.a.id}-${p.b.id}`} className="mb-2 rounded border border-slate-200 p-2" data-testid={`dup-pair-${i}`}>
              <div className="flex items-center gap-2"><span className="font-medium">{p.a.name}</span><span className="text-slate-400">≈</span><span className="font-medium">{p.b.name}</span><span className="ml-auto text-[10px] text-slate-400">{Math.round(p.score * 100)}%</span></div>
              <div className="mt-1 flex gap-2">
                <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => merge(p.a, p.b)} data-testid={`dup-keep-a-${i}`}>Keep “{p.a.name}”</Button>
                <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => merge(p.b, p.a)} data-testid={`dup-keep-b-${i}`}>Keep “{p.b.name}”</Button>
              </div>
            </div>))}
        </>)}
      </div>
    </div>
  );
}
